import { useEffect } from 'react';
import { SupportedCurrencies } from 'vopay';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import InterestHistoryTable from './InterestHistoryTable';
import InterestHistorySearchForm from './InterestHistorySearchForm';
import useFetchInterestAccruedHistory from '@/hooks/useApi/useFetchInterestAccruedHistory';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type InterestHistoryTabProps = {
  hasLongTermAccount: boolean;
  currency: SupportedCurrencies;
};

export default function InterestHistoryTab({ hasLongTermAccount, currency }: InterestHistoryTabProps) {
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();
  const tableID = 'interestHistory';

  const { interestAccruedHistory, fetchInterestAccruedHistory, fetchingInterestAccruedHistory } =
    useFetchInterestAccruedHistory();

  useEffect(() => {
    refreshInterestAccruedHistory();
  }, []);

  async function refreshInterestAccruedHistory(req?: Parameters<typeof fetchInterestAccruedHistory>[0]) {
    try {
      await fetchInterestAccruedHistory(req);
    } catch (err) {
      if (err instanceof Error) {
        alertDialog.error(tGlobal('error'), err.message);
      }
    }
  }

  return (
    <>
      <InterestHistorySearchForm
        tableID={tableID}
        hasLongTermAccount={hasLongTermAccount}
        disabled={fetchingInterestAccruedHistory}
        onSearch={({ accountType, timeRange, timezone: timeZone }) => {
          const startDate = format(new Date(timeRange[0]), 'yyyy-MM-dd');
          const endDate = format(new Date(timeRange[1]), 'yyyy-MM-dd');

          refreshInterestAccruedHistory({
            InterestMaturityType: accountType,
            StartDate: startDate,
            EndDate: endDate,
            Timezone: timeZone,
          });
        }}
      />

      <InterestHistoryTable
        id={tableID}
        interestAccruedDetails={interestAccruedHistory}
        hasLongTermAccount={hasLongTermAccount}
        currency={currency}
        fetching={fetchingInterestAccruedHistory}
      />
    </>
  );
}
